import React, {useState, useCallback} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AdminService} from '@/services/adminService';
import {UserSearchRequest, UserSearchResult} from '@/types/api';
import Card from '@/components/common/Card';
import {UserEditModal} from './UserEditModal';
import {Loader2} from 'lucide-react';

interface Props {
    setPinnedMetrics: (value: any) => void;
}

interface SearchFields {
    firstName: string;
    lastName: string;
    email: string;
    birthday: string;
    phone: string;
}

interface Toast {
    error: boolean;
    message: string;
}

/**
 * UserSearch component - Admin page to let admins search through user data and edit.
 * Refactored to use modern patterns from docs/SUMMARY.md
 */
export const UserSearch: React.FC<Props> = ({setPinnedMetrics}) => {
    const [search, setSearch] = useState<SearchFields>({
        firstName: '',
        lastName: '',
        email: '',
        birthday: '',
        phone: '',
    });

    const [toast, setToast] = useState<Toast>({
        error: false,
        message: '',
    });

    const [isModalHidden, setIsModalHidden] = useState(true);
    const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);

    // React Query mutation for user search
    const searchMutation = useMutation({
        mutationFn: (searchCriteria: UserSearchRequest) => AdminService.searchUsers(searchCriteria),
        onSuccess: (data) => {
            if (data.length === 0) {
                setToast({
                    error: false,
                    message: 'No User Found.',
                });
            } else {
                setToast({error: false, message: ''});
            }
        },
        onError: (error) => {
            console.error('Search error:', error);
            setToast({
                error: true,
                message: 'Error searching users. Please try again.',
            });
        },
    });

    const validateForm = useCallback((): string | null => {
        const phoneReg = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

        if (search.phone && !phoneReg.test(search.phone)) {
            return 'Invalid phone number. Please use this format: ###-###-####';
        }

        if (search.birthday && isNaN(Number(new Date(search.birthday)))) {
            return 'Invalid date of birth. Please use this format: MM/DD/YYYY';
        }

        return null;
    }, [search]);

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            setToast({error: false, message: ''});

            const validationError = validateForm();
            if (validationError) {
                setToast({
                    error: true,
                    message: validationError,
                });
                return;
            }

            searchMutation.mutate(search);
        },
        [search, validateForm, searchMutation]
    );

    const handleInputChange = useCallback(
        (field: keyof SearchFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearch((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        },
        []
    );

    const handleBirthdayChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const digitReg = /^[0-9]$/;
        let value = event.target.value;

        if (value.length === 3 || value.length === 6) {
            if (value[value.length - 1] !== '/') {
                value = value.substring(0, value.length - 1) + '/' + value[value.length - 1];
            } else {
                value = value.substring(0, value.length - 1);
            }
        }

        if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 10)) {
            return;
        }

        setSearch((prev) => ({...prev, birthday: value}));
    }, []);

    const handlePhoneChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const digitReg = /^[0-9]$/;
        let value = event.target.value;

        if (value.length === 4 || value.length === 8) {
            if (value[value.length - 1] !== '-') {
                value = value.substring(0, value.length - 1) + '-' + value[value.length - 1];
            } else {
                value = value.substring(0, value.length - 1);
            }
        }

        if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 12)) {
            return;
        }

        setSearch((prev) => ({...prev, phone: value}));
    }, []);

    const handleEditUser = useCallback((user: UserSearchResult) => {
        setSelectedUser(user);
        setIsModalHidden(false);
    }, []);

    const handleUserUpdated = useCallback(() => {
        // Update the results to reflect the changed data
        if (searchMutation.data) {
            // Force a re-search to get fresh data from the server
            if (search.firstName || search.lastName || search.email || search.birthday || search.phone) {
                searchMutation.mutate(search);
            }
        }
    }, [searchMutation, search]);

    const formatBirthday = useCallback((dateString: string) => {
        if (!dateString) return '';

        // Handle different date formats that might come from the API
        try {
            // If it's already in MM/DD/YYYY format, return as is
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
                return dateString;
            }

            // If it's in ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss), convert to MM/DD/YYYY
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('en-US'); // Returns MM/DD/YYYY format
            }

            // If it's in YYYY-MM-DD format, convert to MM/DD/YYYY
            if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
                const [year, month, day] = dateString.split('T')[0].split('-');
                return `${month}/${day}/${year}`;
            }

            return dateString; // Return as-is if we can't parse it
        } catch {
            return dateString; // Return original if there's an error
        }
    }, []);

    const results = searchMutation.data || [];

    return (
        <div className="space-y-6">
            <Card>
                <Card.Header>
                    <Card.Title>Search for users in database</Card.Title>
                </Card.Header>
                <Card.Content>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label
                                    htmlFor="searchUsers-fname"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name
                                </label>
                                <input
                                    id="searchUsers-fname"
                                    type="text"
                                    value={search.firstName}
                                    placeholder="Optional"
                                    onChange={handleInputChange('firstName')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="searchUsers-lname"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name
                                </label>
                                <input
                                    id="searchUsers-lname"
                                    type="text"
                                    value={search.lastName}
                                    placeholder="Optional"
                                    onChange={handleInputChange('lastName')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="searchUsers-email"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    id="searchUsers-email"
                                    type="email"
                                    value={search.email}
                                    placeholder="Optional"
                                    onChange={handleInputChange('email')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="searchUsers-birthday"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    Date of Birth
                                </label>
                                <input
                                    id="searchUsers-birthday"
                                    type="text"
                                    value={search.birthday}
                                    placeholder="Optional - MM/DD/YYYY"
                                    onChange={handleBirthdayChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="searchUsers-phone"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number
                                </label>
                                <input
                                    id="searchUsers-phone"
                                    type="text"
                                    value={search.phone}
                                    placeholder="Optional - 555-555-5555"
                                    onChange={handlePhoneChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                type="submit"
                                disabled={searchMutation.isPending}
                                className="flex items-center space-x-2 px-4 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90 focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                {searchMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                                <span>{searchMutation.isPending ? 'Searching...' : 'Search Users'}</span>
                            </button>

                            {toast.message && (
                                <div
                                    className="px-3 py-2 rounded-md text-sm text-white"
                                    style={{backgroundColor: toast.error ? '#F15E87' : '#42D4B0'}}>
                                    {toast.message}
                                </div>
                            )}
                        </div>
                    </form>
                </Card.Content>
            </Card>

            {results.length > 0 && (
                <Card>
                    <Card.Header>
                        <Card.Title>Search Results ({results.length} users found)</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Action
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            First Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Birthday
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {results.map((user, index) => (
                                        <tr key={user.id || index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => handleEditUser(user)}
                                                    className="text-mld-primary hover:text-primary-900 font-medium">
                                                    Edit
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {user.firstName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {user.lastName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatBirthday(user.birthday)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card.Content>
                </Card>
            )}

            {selectedUser && !isModalHidden && (
                <UserEditModal
                    isModalHidden={isModalHidden}
                    setIsModalHidden={setIsModalHidden}
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                    setPinnedMetrics={setPinnedMetrics}
                    onUserUpdated={handleUserUpdated}
                />
            )}
        </div>
    );
};

export default UserSearch;
